import React from "react";
import { motion } from "framer-motion";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const monogram = (name) =>
  (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();

export default function IntroLogo({ name, logoUrl }) {
  if (logoUrl) return <WideLogo name={name} src={`${BASE}${logoUrl}`} />;
  return (
    <motion.div
      data-testid="veroogle-intro-logo"
      className="relative w-28 h-28 sm:w-32 sm:h-32"
      initial={{ scale: 0.3, opacity: 0, rotate: -20, filter: "blur(16px)" }}
      animate={{ scale: 1, opacity: 1, rotate: 0, filter: "blur(0px)" }}
      transition={{ delay: 0.25, type: "spring", stiffness: 180, damping: 14, filter: { type: "tween", delay: 0.25, duration: 0.5, ease: "easeOut" }, opacity: { delay: 0.25, duration: 0.3 } }}
    >
      <motion.div
        className="absolute -inset-6 rounded-full bg-gradient-to-tr from-cyan-500/40 via-emerald-400/30 to-blue-600/40 blur-2xl"
        animate={{ opacity: [0.4, 1, 0.6], scale: [0.8, 1.15, 1] }}
        transition={{ delay: 0.3, duration: 1.4 }}
      />
      <motion.div
        className="absolute -inset-[3px] rounded-[28px]"
        style={{ background: "conic-gradient(from 0deg, #06b6d4, #10b981, #3b82f6, #06b6d4)" }}
        animate={{ rotate: 360 }}
        transition={{ duration: 3, ease: "linear", repeat: Infinity }}
      />
      <div className="relative w-full h-full rounded-[26px] bg-[#070C1A] flex items-center justify-center overflow-hidden">
        <span className="text-4xl sm:text-5xl font-black bg-gradient-to-br from-cyan-300 via-white to-emerald-300 bg-clip-text text-transparent" data-testid="veroogle-intro-monogram">
          {monogram(name)}
        </span>
        <Shine />
      </div>
    </motion.div>
  );
}

function Shine() {
  return (
    <motion.div
      className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/70 to-transparent skew-x-[-20deg] pointer-events-none"
      initial={{ x: "-150%" }}
      animate={{ x: "350%" }}
      transition={{ delay: 0.8, duration: 0.7, ease: "easeInOut" }}
    />
  );
}

function WideLogo({ name, src }) {
  return (
    <motion.div
      data-testid="veroogle-intro-logo"
      className="relative px-6 py-4"
      initial={{ scale: 0.5, opacity: 0, filter: "blur(18px)" }}
      animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }}
      transition={{ delay: 0.25, type: "spring", stiffness: 160, damping: 15, filter: { type: "tween", delay: 0.25, duration: 0.55, ease: "easeOut" }, opacity: { delay: 0.25, duration: 0.3 } }}
    >
      <motion.div
        className="absolute inset-0 rounded-full bg-gradient-to-r from-cyan-500/30 via-emerald-400/25 to-orange-500/25 blur-3xl"
        animate={{ opacity: [0.3, 1, 0.55], scale: [0.7, 1.2, 1] }}
        transition={{ delay: 0.3, duration: 1.4 }}
      />
      <div className="relative">
        <img src={src} alt={name} data-testid="veroogle-intro-logo-img"
          className="relative h-16 sm:h-24 w-auto max-w-[82vw] object-contain drop-shadow-[0_0_24px_rgba(6,182,212,0.35)]" />
        <div
          aria-hidden
          className="absolute inset-0 overflow-hidden pointer-events-none"
          style={{ WebkitMaskImage: `url(${src})`, maskImage: `url(${src})`, WebkitMaskSize: "100% 100%", maskSize: "100% 100%" }}
        >
          <Shine />
        </div>
      </div>
    </motion.div>
  );
}
